// Storage usage bar — compact stat card for the Billing page.
// Shows: used / quota, percentage bar, file count, status pill (ok / warn / full).
import React from 'react';
import { HardDrive, AlertTriangle } from 'lucide-react';

function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function StorageUsageBar({ storage, onManage }) {
  if (!storage) return null;
  const { used_bytes = 0, quota_bytes = 0, used_pct = 0, file_count = 0 } = storage;
  // Precise percentage from raw bytes so tiny-but-real usage (e.g. 15 MB of
  // imported Jira attachments on a 10 GB quota = 0.15%) isn't displayed as a
  // flat "0%". We show "<1%" with a thin visible bar sliver instead.
  const rawPct = quota_bytes > 0 ? (used_bytes / quota_bytes) * 100 : 0;
  const pctLabel =
    used_bytes === 0 ? '0%' :
    rawPct < 1 ? '<1%' :
    rawPct < 10 ? `${rawPct.toFixed(1)}%` :
    `${Math.round(rawPct)}%`;
  const barWidth = used_bytes > 0 ? Math.max(1.5, Math.min(100, rawPct)) : 0;
  const tone =
    rawPct >= 95 ? 'red' :
    rawPct >= 80 ? 'amber' : 'emerald';
  const toneText = { red: 'text-red-700', amber: 'text-amber-700', emerald: 'text-emerald-700' }[tone];
  const toneBar = { red: 'bg-red-500', amber: 'bg-amber-500', emerald: 'bg-emerald-500' }[tone];
  const toneBg = { red: 'bg-red-50 border-red-200', amber: 'bg-amber-50 border-amber-200', emerald: 'bg-zinc-50 border-zinc-200' }[tone];

  return (
    <div className={`rounded-2xl border ${toneBg} p-5`} data-testid="storage-usage-bar">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
            <HardDrive size={18} className="text-zinc-700" />
          </div>
          <div>
            <h3 className="font-['Outfit'] font-bold text-zinc-900 text-[15px]">Storage usage</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">{file_count.toLocaleString()} file{file_count === 1 ? '' : 's'} stored across your workspace</p>
          </div>
        </div>
        <div className={`text-right ${toneText}`}>
          <div className="text-[20px] font-black tabular-nums leading-none" data-testid="storage-used-pct">{pctLabel}</div>
          <div className="text-[10px] uppercase tracking-wide font-bold opacity-70 mt-1">used</div>
        </div>
      </div>
      <div className="h-2 rounded-full bg-white border border-zinc-200 overflow-hidden">
        <div className={`h-full ${toneBar} transition-all duration-500`} style={{ width: `${barWidth}%` }} />
      </div>
      <div className="flex items-center justify-between mt-3 text-[12px]">
        <span className="text-zinc-700">
          <strong className="tabular-nums">{fmtBytes(used_bytes)}</strong>
          <span className="text-zinc-400"> / {fmtBytes(quota_bytes)}</span>
        </span>
        {tone !== 'emerald' && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${toneText}`}>
            <AlertTriangle size={11} />
            {tone === 'red' ? 'Quota exceeded — upgrade to continue' : 'Approaching quota — consider upgrading'}
          </span>
        )}
        {onManage && (
          <button onClick={onManage} className="text-[11px] text-blue-600 hover:underline font-medium" data-testid="storage-manage-link">
            Manage storage →
          </button>
        )}
      </div>
    </div>
  );
}
